/* eslint-disable complexity */
/* eslint-disable no-var */

/* eslint-disable max-lines-per-function */
define(function (require, exports, module) {
    'use strict';

    const { groupBy } = require('ramda');
    const api = require('Cloud/Application/api');
    const _ = require('Cloud/Application/_');
    const app = require('Cloud/Application/app');
    const apiError = require('Cloud/API/apiError');
    const UploadReason = require('../../UploadReason');
    const OverQuotaFail = require('../../fails/OverQuotaFail');
    const ConnectionFail = require('../../fails/ConnectionFail');
    const NameTooLongFail = require('../../fails/NameTooLongFail');
    const PathTooLongFail = require('../../fails/PathTooLongFail');
    const FileExistsFail = require('../../fails/FileExistsFail');
    const ViolatedFilenameFail = require('../../fails/ViolatedFilenameFail');
    const InvalidCharactesFail = require('../../fails/InvalidCharactesFail');
    const ReadOnlyDirectoryFail = require('../../fails/ReadOnlyDirectoryFail');
    const WrongDestinationPathFail = require('../../fails/WrongDestinationPathFail');
    const UserFileSizeLimitFail = require('../../fails/UserFileSizeLimitFail');
    const HttpError = require('../../errors/HttpError');
    const features = require('Cloud/Application/Features');
    const PublicFileSizeLimit = require('../../fails/PublicFileSizeLimit');
    const escapeRegExp = require('lodash.escaperegexp');
    const config = require('Cloud/config');
    const { store: reduxStore } = require('reactApp/store');
    const { addFilesSuccess, updateItem } = require('reactApp/modules/modifying/modifying.actions');
    const { getHomeItemById } = require('reactApp/modules/home/home.selectors');
    const { getItemById, getCurrentFolder } = require('reactApp/modules/storage/storage.selectors');
    const { getCurrentStorage } = require('reactApp/modules/router/router.selectors');
    const { updateUploadFilesAction } = require('reactApp/modules/uploadList/uploadList.module');
    const { sendGaUploader } = require('reactApp/modules/uploadList/uploadList.helpers');
    const { setUserFileSizeLimit } = require('reactApp/modules/uploadList/uploadList.module');
    const { EFileError, EFileStatus } = require('reactApp/modules/uploadList/uploadList.model');
    const { ActionsBatcher } = require('reactApp/utils/reduxHelpers');
    const { getInputFile } = require('reactApp/modules/uploadList/uploadList.selectors');
    const { getPublicUploadLimit } = require('reactApp/modules/public/public.selectors');
    const { isOwnPublic } = require('reactApp/modules/public/public.selectors');
    const { addFolderHelper } = require('reactApp/modules/modifying/modifying.helpers');

    const IS_REACT_PAGE = config.get('REACT_PAGE');
    const IS_PUBLIC = config.get('PUBLIC');

    const actionsBatcher = new ActionsBatcher((items) => {
        const list = groupBy((item) => item.parent, items);
        Object.keys(list).forEach((parent) => {
            const group = list[parent];
            const inputItems = [];
            const namesHashTable = {};
            const state = reduxStore.getState();
            group.forEach((itemData) => {
                itemData.items.forEach((item) => {
                    if (!namesHashTable[item.name]) {
                        const currentItem = getInputFile(state, {
                            cloudPath: item.home,
                            descriptorId: item.descriptorId,
                        });
                        // Изза батчинга addFilesSuccess может прилететь после выставления статуса ошибки и файл будет добавлен,
                        // хотя с ошибками мы должны скрывать. Потому дополнительно проверим, не появился ли у него статус ошибки.
                        if (!currentItem || currentItem.status !== 'error') {
                            inputItems.push(item);
                            namesHashTable[item.name] = true;
                        }
                    }
                });
            });
            reduxStore.dispatch(
                addFilesSuccess({
                    items: inputItems,
                    storage: group[0].storage,
                    parent: group[0].parent,
                })
            );
        });
    });

    /**
     * @namespace
     */
    var cloudFileSystem = {
        /**
         * @type {string}
         */
        EMPTY_FILE_HASH: '0000000000000000000000000000000000000000',

        /**
         * @returns {string}
         */
        getWorkingDirectory() {
            const path = IS_REACT_PAGE ? getCurrentFolder(reduxStore.getState()) : app.currentFolder();

            return path && path.id;
        },

        getCurrentFolder() {
            return IS_REACT_PAGE || IS_PUBLIC ? getCurrentFolder(reduxStore.getState()) : app.currentFolder();
        },

        /**
         * @param {string} path
         * @returns {boolean}
         */
        isReadOnlyDirectory(path) {
            if (IS_REACT_PAGE) {
                const folder = getItemById(reduxStore.getState(), path);
                return folder && folder.isReadOnly;
            }

            const directory = app.get(path);
            return directory && directory.readonly;
        },

        /**
         * @returns {Promise}
         * @resolves {number} – in bytes
         * @rejects {Error}
         */
        getUserFileSizeLimit() {
            return new Promise(function (resolve) {
                const limit = app.user.getFileSizeLimit();

                resolve(limit);
            });
        },

        /**
         * @returns {number} - in bytes
         */
        getPublicFileSizeLimit() {
            const state = reduxStore.getState();
            const uploadLimit = getPublicUploadLimit(state);

            return uploadLimit || features.getFeatureParam('public-upload', 'sizeLimit', 2147483648);
        },

        /**
         * @param {number} size – in bytes
         * @returns {Promise}
         * @resolves {number} – size
         * @rejects {UploadReason|Error}
         */
        checkFileSizeLessThanUserLimit(size) {
            let promise = cloudFileSystem.getUserFileSizeLimit();

            promise = promise.then(function (limit) {
                let promise;

                if (size > limit) {
                    reduxStore.dispatch(setUserFileSizeLimit(limit));

                    const source = UploadReason.SOURCE_WEB_CLIENT;
                    const stack = new Error('UserFileSizeLimitFail');
                    const reason = new UserFileSizeLimitFail(stack, source, limit);

                    promise = Promise.reject(reason);
                } else {
                    promise = Promise.resolve(size);
                }

                return promise;
            });

            return promise;
        },

        /**
         * @param {number} size – in bytes
         * @returns {Promise}
         * @resolves {number} – size
         * @rejects {UploadReason|Error}
         */
        checkFileSizeLessThanPublicLimit: (size) => {
            return new Promise((resolve, reject) => {
                const publicLimit = cloudFileSystem.getPublicFileSizeLimit();

                if (size < publicLimit) {
                    resolve(size);
                } else {
                    const source = UploadReason.SOURCE_WEB_CLIENT;
                    const stack = new Error('PublicFileSizeLimitFail');
                    const reason = new PublicFileSizeLimit(stack, source, publicLimit);
                    reject(reason);
                }
            });
        },

        /**
         * @param {number} size – in bytes
         * @returns {Promise}
         * @resolves {number} – size
         * @rejects {UploadReason|Error}
         */
        checkFileSizeLessThanLimit(size) {
            return cloudFileSystem.checkFileSizeLessThanUserLimit(size);
        },

        /**
         * @param {number} size – in bytes
         * @returns {Promise}
         * @resolves {number} – free space in bytes
         * @rejects {UploadReason|Error}
         */
        checkEnoughSpace(size) {
            return new Promise(function (resolve, reject) {
                const space = app.user.getSpace();
                const free = space.bytesFree;

                if (free < size) {
                    const source = UploadReason.SOURCE_WEB_CLIENT;
                    const stack = new Error('OverQuotaFail');
                    const reason = new OverQuotaFail(stack, source);

                    reject(reason);
                } else {
                    resolve(free);
                }
            });
        },

        addItemToStore({ cloudPath = '', descriptorId = null, workingDir, hash = null, size, onlyRedux = false, itemStorage }) {
            const pathParts = cloudPath.split('/'); // → ['', 'folder1', 'folder2', 'filename.ext']

            const state = reduxStore.getState();
            pathParts.reduce(function (parent, name) {
                const path = `${parent}/${name}`;
                const storage = getCurrentStorage(state);
                const cloudItem = IS_REACT_PAGE ? getHomeItemById(state, path) : app.get(path, storage);
                const isEqualPath = path === cloudPath;
                const sizeCheck = size > 0 ? size : 0;
                const fakeCloudItem = {
                    id: path,
                    descriptorId,
                    weblink: IS_PUBLIC ? path : null,
                    home: IS_PUBLIC ? '' : path,
                    name,
                    kind: 'file',
                    hash,
                    size: sizeCheck,
                    mtime: Math.floor(Date.now() / 1000),
                    virus_scan: 'not_yet',
                    type: 'file',
                    attr: IS_PUBLIC
                        ? {
                              actor: app.user.getEmail(),
                          }
                        : void 0,
                };

                if (path === workingDir) {
                    return path;
                }

                if (cloudItem) {
                    if (isEqualPath && !IS_REACT_PAGE && !onlyRedux) {
                        app.updateFile(fakeCloudItem, storage);
                    }

                    const cloudItemSize = sizeCheck > 0 ? sizeCheck : cloudItem?.size;
                    const cloudItemCopy = { ...cloudItem, size: cloudItemSize, mtime: Math.floor(Date.now() / 1000) }; // cloudItem readonly object
                    reduxStore.dispatch(updateItem(cloudItemCopy));
                    return path;
                }

                if (!parent) {
                    parent = '/';
                }

                if (isEqualPath) {
                    if (IS_REACT_PAGE || IS_PUBLIC || onlyRedux) {
                        // не добавляем пока в старое дерево
                        actionsBatcher.push({
                            parent,
                            storage,
                            items: [
                                {
                                    ...fakeCloudItem,
                                    name: fakeCloudItem.name || fakeCloudItem.nameWithoutExt,
                                    kind: 'file',
                                },
                            ],
                        });
                    } else {
                        app.addFile(fakeCloudItem, parent, true, storage);
                    }
                } else {
                    fakeCloudItem.parent = parent;
                    fakeCloudItem.kind = 'folder';
                    fakeCloudItem.type = 'folder';
                    fakeCloudItem.size = 0;
                    fakeCloudItem.count = {
                        folders: 0,
                        files: 0,
                    };
                    delete fakeCloudItem.hash;
                    delete fakeCloudItem.virus_scan;
                    delete fakeCloudItem.attr;

                    if (IS_REACT_PAGE || onlyRedux || IS_PUBLIC) {
                        // не добавляем пока в старое дерево
                        actionsBatcher.push({
                            items: [
                                {
                                    ...fakeCloudItem, /// ??? bug: folder has the same descriptorId as its first file
                                    kind: 'folder',
                                    name: fakeCloudItem.name || fakeCloudItem.id.split('/').pop(),
                                    count: {
                                        folders: 0,
                                        files: 0,
                                    },
                                },
                            ],
                            parent,
                        });
                    } else {
                        app.addFolder(fakeCloudItem, parent, true, void 0, storage);
                    }
                }

                return path;
            });
        },

        /**
         * @param {string} path
         * @param {string} hash – cloudHash (SHA1 + salt)
         * @param {number} size – in bytes
         * @param {string} conflictResolution – in bytes
         * @param {string} descriptorId
         * @param {string} workingDirectoryArg
         * @returns {Promise}
         * @resolves {string} – cloudPath
         * @rejects {UploadReason|Error}
         */
        addFile(path, hash, size, conflictResolution, descriptorId = '', workingDirectoryArg = '', isFolder = false) {
            // TODO: retries
            const currentFolder = this.getCurrentFolder();
            const isOwn = isOwnPublic(reduxStore.getState());
            const ownPublicFolderWeblink = isOwn && currentFolder && currentFolder.weblink;
            let workingDirectory = workingDirectoryArg || cloudFileSystem.getWorkingDirectory();
            const RE = new RegExp(`(${escapeRegExp(workingDirectory)}).+(/.*)`, 'gi');
            // Менеджмент решил, что если заливать в паблик папку, то не нужно создавать эту папку в облаке, а просто заливать ее содержимое в текущую директорию
            // Просто удалите регулярку, когда решение поменяется :)

            const pathTo =
                IS_PUBLIC && !isOwn && path && isFolder ? path.replace(RE, (str, workingDir, fileName) => workingDir + fileName) : path;

            let promise = new Promise(function (resolve, reject) {
                const fileAdd = IS_PUBLIC && !isOwn ? api.weblinks.file.add : api.file.add;
                const params = {
                    hash,
                    size,
                };

                if (IS_PUBLIC && !isOwn) {
                    params.weblink = `/${pathTo}`;
                    params.conflict = 'rename';
                } else {
                    params.home = pathTo;
                    params.conflict = conflictResolution || 'strict';
                }

                const deferred = fileAdd(params);

                deferred.done(function (cloudPath) {
                    resolve(cloudPath);
                });

                deferred.fail(function (error, status, response) {
                    reject(response);
                });
            });

            promise = promise.catch(function (error) {
                const source = UploadReason.SOURCE_WEB_BACKEND;
                let stack;
                let reason;
                let fileError;
                let fileStatus = EFileStatus.ERROR;
                const xhr = error.getXHR();
                const status = xhr.status;

                if (status) {
                    const isBadRequest = status === HttpError.BAD_REQUEST;
                    const isInsufficient = status === HttpError.INSUFFICIENT_STORAGE || HttpError.INSUFFICIENT_STORAGE_PREV;

                    if (isBadRequest || isInsufficient) {
                        const data = error.getData();
                        const errorCode = apiError.getCode(data);

                        switch (errorCode) {
                            case ViolatedFilenameFail.WEB_BACKEND_ERROR:
                                stack = new Error('ViolatedFilenameFail');
                                reason = new ViolatedFilenameFail(stack, source);
                                fileError = EFileError.VIOLATED_FILENAME;

                                break;

                            case NameTooLongFail.WEB_BACKEND_ERROR:
                                stack = new Error('NameTooLongFail');
                                reason = new NameTooLongFail(stack, source);
                                fileError = EFileError.NAME_TOO_LONG;

                                break;

                            case InvalidCharactesFail.WEB_BACKEND_ERROR:
                                stack = new Error('InvalidCharactesFail');
                                reason = new InvalidCharactesFail(stack, source);
                                fileError = EFileError.INVALID_CHARACTERS;

                                break;

                            case ReadOnlyDirectoryFail.WEB_BACKEND_ERROR:
                                stack = new Error('ReadOnlyDirectoryFail');
                                reason = new ReadOnlyDirectoryFail(stack, source);

                                break;

                            case WrongDestinationPathFail.WEB_BACKEND_ERROR:
                                stack = new Error('WrongDestinationPathFail');
                                reason = new WrongDestinationPathFail(stack, source);
                                fileError = EFileError.UNKNOWN;

                                break;

                            case OverQuotaFail.WEB_BACKEND_ERROR:
                                stack = new Error('OverQuotaFail');
                                reason = new OverQuotaFail(stack, source);
                                fileStatus = EFileStatus.WARNING;
                                fileError = EFileError.OVER_QUOTA_CLOUD;

                                break;

                            case FileExistsFail.WEB_BACKEND_ERROR:
                                stack = new Error('FileExistsFail');
                                reason = new FileExistsFail(stack, source);

                                break;
                        }
                    }

                    if (!reason) {
                        stack = new Error('HttpError');
                        reason = new HttpError(stack, source, status, xhr.responseText);
                        fileError = EFileError.CONNECTION_ERROR;
                    }
                } else {
                    stack = new Error('ConnectionFail');
                    reason = new ConnectionFail(stack, source);
                    fileError = EFileError.CONNECTION_ERROR;
                }

                if (fileError && descriptorId) {
                    // TODO убрать отсюда обработку ошибок
                    reduxStore.dispatch(
                        updateUploadFilesAction({
                            descriptorId,
                            cloudPath: path,
                            status: fileStatus,
                            error: fileError,
                        })
                    );

                    sendGaUploader('file', fileError);
                }

                return Promise.reject(reason);
            });

            promise = promise.then(function (cloudPath) {
                workingDirectory = workingDirectoryArg || cloudFileSystem.getWorkingDirectory();
                let currentWorkingDir = workingDirectory;
                if (isOwn) {
                    // загрузили все в хомяк, но мы в паблике и фейк элементы надо добавлять туда
                    cloudPath = cloudPath.replace(workingDirectory, ownPublicFolderWeblink);
                    currentWorkingDir = ownPublicFolderWeblink;
                }

                cloudFileSystem.addItemToStore({ cloudPath, workingDir: currentWorkingDir, hash, size });

                return Promise.resolve(cloudPath);
            });

            return promise;
        },

        /**
         * @param {string} path
         * @returns {Promise}
         * @resolves {string} – cloudPath
         * @rejects {UploadReason|Error}
         */
        addFolder(path) {
            // TODO: retries
            let promise = new Promise(function (resolve, reject) {
                const deferred = api.folder.add({
                    home: path,
                    conflict: 'ignore', // TODO: strict
                });

                deferred.done(function (data) {
                    if (typeof data === 'string') {
                        resolve(data);
                    } else {
                        resolve(data.id);
                    }
                });

                deferred.fail(function (error, status, response) {
                    reject(response);
                });
            });

            promise = promise.catch(function (error) {
                const source = UploadReason.SOURCE_WEB_BACKEND;
                let stack;
                let reason;
                const xhr = error.getXHR();
                const status = xhr.status;

                if (status) {
                    const isBadRequest = status === HttpError.BAD_REQUEST;
                    const isReadOnly = status === HttpError.INSUFFICIENT_STORAGE || status === HttpError.INSUFFICIENT_STORAGE_PREV;

                    if (isBadRequest || isReadOnly) {
                        const data = error.getData();
                        const errorCode = apiError.getCode(data);

                        switch (errorCode) {
                            case PathTooLongFail.WEB_BACKEND_ERROR:
                                stack = new Error('PathTooLongFail');
                                reason = new PathTooLongFail(stack, source);

                                break;

                            case InvalidCharactesFail.WEB_BACKEND_ERROR:
                                stack = new Error('InvalidCharactesFail');
                                reason = new InvalidCharactesFail(stack, source);

                                break;

                            case ReadOnlyDirectoryFail.WEB_BACKEND_ERROR:
                                stack = new Error('ReadOnlyDirectoryFail');
                                reason = new ReadOnlyDirectoryFail(stack, source);

                                break;

                            case WrongDestinationPathFail.WEB_BACKEND_ERROR:
                                stack = new Error('WrongDestinationPathFail');
                                reason = new WrongDestinationPathFail(stack, source);

                                break;

                            case 'exists':
                                // TODO: const, conflict strict
                                break;
                        }
                    }

                    if (!reason) {
                        stack = new Error('HttpError');
                        reason = new HttpError(stack, source, status, xhr.responseText);
                    }
                } else {
                    stack = new Error('ConnectionFail');
                    reason = new ConnectionFail(stack, source);
                }

                return Promise.reject(reason);
            });

            promise = promise.then(function (cloudPath) {
                const workingDirectory = cloudFileSystem.getWorkingDirectory();
                const pathParts = cloudPath.split('/'); // → ['', 'folder1', 'folder2']

                const storage = getCurrentStorage(reduxStore.getState());

                pathParts.reduce(function (parent, name) {
                    const path = `${parent}/${name}`;
                    const cloudItem = IS_REACT_PAGE ? getHomeItemById(reduxStore.getState(), path) : app.get(path, storage);

                    if (path !== workingDirectory && !cloudItem) {
                        if (!parent) {
                            parent = '/';
                        }

                        const fakeCloudItem = {
                            id: path,
                            parent,
                            name,
                            kind: 'folder',
                            mtime: Math.floor(Date.now() / 1000),
                        };

                        if (app.addFolder) {
                            app.addFolder(fakeCloudItem, parent, true, void 0, storage);
                        } else {
                            addFolderHelper({
                                item: fakeCloudItem,
                                parent,
                                skipXhr: true,
                                conflictMode: void 0,
                                storage,
                            });
                        }
                    }

                    return path;
                });

                return Promise.resolve(cloudPath);
            });

            return promise;
        },
    };

    module.exports = cloudFileSystem;
});
